import { useRef, useState } from "react";
import { useGuestCompanions } from "@/providers/GuestCompanions";
import { AcceptanceState } from "@/types/enums/AcceptanceState";
import { Guest } from "@/types/Guest";
import { fetchGuestDeleteImpact } from "@/lib/api/handlers/GuestHandling";
import { isPartyMember } from "@/lib/guests/party";
import type { GuestDeleteImpact } from "@/types/GuestDeleteImpact";
import { isTrashDeleteResult } from "@/types/GuestTrash";
import { armGuestTrashUndo } from "@/lib/guests/guestTrashUndo";

/**
 * Which of the three kinds of row is this?
 *
 * The panel can show rows that are NOT part of the party — an undecided
 * request, and (when the organiser asks for them) a declined or archived one.
 * They need different verbs, and deciding that from the row's own state is what
 * stops one icon from meaning two things: the trash used to be wired to the
 * decline handler, so "löschen" wrote DECLINED and the row came back on the
 * next read.
 */
export type CompanionRowKind = "party" | "request" | "inactive";

export function companionRowKind(companion: Guest): CompanionRowKind {
  // The party question is NOT re-answered here — `isPartyMember` is the guest
  // list's own rule, mirrored from `GuestRepository.activeCompanionPredicate`.
  // A lookalike here is what dropped the `state == WAITLISTED` arm and left a
  // waitlisted companion with approve/reject icons and no way out.
  if (isPartyMember(companion)) return "party";
  if (companion.archived === true) return "inactive";
  return companion.acceptanceState === AcceptanceState.PENDING
    ? "request"
    : "inactive";
}

export const useCompanionEntry = (companionProps: Guest, hostGuestId?: string) => {
  const [companion, setCompanion] = useState<Guest>(companionProps);
  const companionDb = useRef<Guest>(companionProps);
  const [isDeleting, setIsDeleting] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  /** Surfaced INSIDE the confirmation dialog. A delete that failed silently and
   *  closed the dialog reads exactly like a delete that worked. */
  /** The RAW failure of the last row action — the row translates it, per-row
   *  reasons included (M-01d). It used to keep only `.message`, which for the
   *  guest batch 400 is the envelope, never the reason. */
  const [actionError, setActionError] = useState<unknown>(null);
  /** undefined = still loading, null = could not be determined. */
  const [deleteImpact, setDeleteImpact] = useState<
    GuestDeleteImpact | null | undefined
  >(undefined);
  const {
    acceptCompanion,
    declineCompanion,
    deleteCompanion,
    restoreCompanion,
    setAllCompanionsState,
  } = useGuestCompanions();

  const rowKind = companionRowKind(companion);

  const formatCompanionName = (noName: string) => {
    return (
      (
        (companion.title ? companion.title + " " : "") +
        (companion.firstName ? companion.firstName + " " : "") +
        (companion.lastName ? companion.lastName : "")
      ).trim() || noName
    );
  };

  const handleOpenDeleteConfirmation = () => {
    setActionError(null);
    setIsDeleting(true);
  };

  /**
   * Open the DELETE confirmation and start loading what the delete will take
   * with it (seats, sessions, sent mails). Same two-step the guest toolbar's
   * own delete uses — the summary is what turns "kann nicht rückgängig gemacht
   * werden" from a warning into a statement the organiser can check.
   */
  const handleOpenHardDeleteConfirmation = (eventId: string) => {
    setActionError(null);
    setIsDeleting(true);
    setDeleteImpact(undefined);
    // No id, no impact to ask for — settle on "unknown" instead of leaving
    // the dialog waiting (its confirm is off while the impact is pending).
    if (!companion.guestId) {
      setDeleteImpact(null);
      return;
    }
    void fetchGuestDeleteImpact(eventId, {
      mode: "ids",
      ids: [companion.guestId],
    }).then(setDeleteImpact);
  };

  const handleCloseDeleteConfirmation = () => {
    setIsDeleting(false);
    setActionError(null);
  };

  const handleDeclineCompanion = async () => {
    try {
      setLocalLoading(true);
      await declineCompanion(companion.guestId!, companion.mainGuest);

      // Remove the companion from all invitees' companions lists
      setAllCompanionsState((prev) => {
        const newMap = new Map(prev);

        // Search through all invitees to find and remove the companion
        newMap.forEach((companions, guestId) => {
          if (!companions) return;

          // Check if this invitee has the companion we want to remove
          const hasCompanion = companions.some(
            (comp) => comp.guestId === companion.guestId
          );

          if (hasCompanion) {
            // Remove the declined companion from the list
            const filteredCompanions = companions.filter(
              (comp) => comp.guestId !== companion.guestId
            );
            newMap.set(guestId, filteredCompanions);
          }
        });

        return newMap;
      });

      setLocalLoading(false);
      setIsDeleting(false);
    } catch (error) {
      // Reported, not swallowed. This used to close the dialog on failure,
      // which reads exactly like a successful rejection — the same illusion
      // the delete lane is careful to avoid.
      setActionError(error ?? new Error("unknown"));
      setLocalLoading(false);
    }
  };

  /**
   * The real thing: the guest-delete lane, with its whole cascade.
   *
   * Unlike its decline twin above this does NOT swallow the failure — the
   * dialog stays open and says what went wrong, because the row disappearing
   * is the only feedback a delete gives and a silent failure would fake it.
   */
  const handleDeleteCompanion = async (
    _e?: unknown,
    _typed?: string,
    options?: { eventId?: string },
  ) => {
    setLocalLoading(true);
    setActionError(null);
    try {
      const result = await deleteCompanion(companion.guestId!, companion.mainGuest ?? hostGuestId);
      // W6: a TRASH answer arms the 15 s undo pill of this event.
      if (options?.eventId && isTrashDeleteResult(result)) {
        armGuestTrashUndo({
          eventId: options.eventId,
          batchId: result.trashBatchId,
          count: result.count,
          purgeAt: result.purgeAt ?? null,
        });
      }
      setIsDeleting(false);
    } catch (error) {
      setActionError(error ?? new Error("unknown"));
    } finally {
      setLocalLoading(false);
    }
  };

  const handleApproveCompanion = async () => {
    setLocalLoading(true);
    setActionError(null);
    try {
      await acceptCompanion(companion.guestId!, companion.mainGuest);
      companionDb.current = {
        ...companion,
        acceptanceState: AcceptanceState.ACCEPTED,
      };
      setCompanion({ ...companion, acceptanceState: AcceptanceState.ACCEPTED });
    } catch (error) {
      // Approve has no dialog to report into, so the row itself carries it —
      // otherwise a refused approval (an exhausted contingent is the ordinary
      // one) is a spinner that stops and nothing else.
      setActionError(error ?? new Error("unknown"));
    } finally {
      setLocalLoading(false);
    }
  };

  /** Put a removed row back into the party. */
  const handleRestoreCompanion = async () => {
    setLocalLoading(true);
    setActionError(null);
    try {
      await restoreCompanion(
        companion.guestId!,
        companion.mainGuest ?? hostGuestId,
      );
      // Mirror exactly what the restore did, field for field. Flipping only
      // `acceptanceState` left an unarchived row still saying "Archived" and
      // still offering Restore — a successful restore that looked like a
      // failed one. And the acceptance flip only happened if the row was
      // DECLINED: `restoreCompanion` does not touch a SYSTEM row's acceptance.
      const restored: Guest = {
        ...companion,
        archived: false,
        acceptanceState:
          companion.acceptanceState === AcceptanceState.DECLINED
            ? AcceptanceState.ACCEPTED
            : companion.acceptanceState,
      };
      companionDb.current = restored;
      setCompanion(restored);
    } catch (error) {
      // It used to have no catch at all, and the caller discards the promise —
      // so a refused restore was an unhandled rejection, a spinner that stopped
      // and nothing else. Like approve, it has no dialog, so the row says it.
      setActionError(error ?? new Error("unknown"));
    } finally {
      setLocalLoading(false);
    }
  };

  return {
    companion,
    rowKind,
    isDeleting,
    localLoading,
    actionError,
    deleteImpact,
    handleOpenDeleteConfirmation,
    handleOpenHardDeleteConfirmation,
    handleCloseDeleteConfirmation,
    handleApproveCompanion,
    handleDeclineCompanion,
    handleDeleteCompanion,
    handleRestoreCompanion,
    formatCompanionName,
  };
};
